import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BehaviorSubject } from 'rxjs';

type QueryParam = { key: string; value: string };

export abstract class QueryParamsManager {
	abstract get(key: string): string | undefined;
	abstract set(key: string, value: string): void;
	abstract delete(key: string): void;
}

export class QueryParamsManagerImpl implements QueryParamsManager {
	private static instance: QueryParamsManagerImpl | undefined;

	public wantedState: BehaviorSubject<QueryParam[]>;

	public readonly pendingKeys = new Set<string>();

	constructor() {
		const qs = new URLSearchParams(window.location.search);
		const initialQS: QueryParam[] = [];
		qs.forEach((value, key) => initialQS.push({ key, value }));
		this.wantedState = new BehaviorSubject<QueryParam[]>(initialQS);
	}

	static getInstance(): QueryParamsManagerImpl {
		if (!QueryParamsManagerImpl.instance) {
			QueryParamsManagerImpl.instance = new QueryParamsManagerImpl();
			return QueryParamsManagerImpl.instance;
		}
		return QueryParamsManagerImpl.instance;
	}

	/**
	 * Drops the singleton so the next `getInstance()` re-reads `window.location.search`.
	 * Used by the test setup: the instance keeps `wantedState` across tests sharing a page.
	 */
	static reset(): void {
		QueryParamsManagerImpl.instance = undefined;
	}

	// eslint-disable-next-line class-methods-use-this
	get(key: string): string | undefined {
		const qs = new URLSearchParams(window.location.search.slice(1));
		return qs.get(key) ?? undefined;
	}

	set(key: string, value: string): void {
		this.pendingKeys.add(key);
		const existingIndex = this.wantedState.getValue().findIndex(({ key: internalKey }) => internalKey === key);
		if (existingIndex === -1) {
			this.wantedState.next([...this.wantedState.getValue(), { key, value }]);
		} else {
			const tmp = structuredClone(this.wantedState.getValue());
			tmp[existingIndex] = { key, value };
			this.wantedState.next(tmp);
		}
	}

	delete(key: string): void {
		this.pendingKeys.add(key);
		const existingIndex = this.wantedState.getValue().findIndex(({ key: internalKey }) => internalKey === key);
		if (existingIndex !== -1) {
			this.wantedState.next([
				...this.wantedState.getValue().slice(0, existingIndex),
				...this.wantedState.getValue().slice(existingIndex + 1),
			]);
		}
	}
}

function serializeQueryParams(params: QueryParam[]): string {
	const search = params.reduce(
		(acc, { key, value }) => `${acc}${acc !== '' ? '&' : ''}${key}${value ? `=${encodeURIComponent(value)}` : ''}`,
		'',
	);
	return search ? `?${search}` : '';
}

function parseQueryParams(search: string): QueryParam[] {
	const params: QueryParam[] = [];
	new URLSearchParams(search).forEach((value, key) => params.push({ key, value }));
	return params;
}

function adoptSearch(search: string, wanted: QueryParam[], pendingKeys: Set<string>): QueryParam[] {
	const fromUrl = parseQueryParams(search).filter(({ key }) => !pendingKeys.has(key));
	const stillPending = wanted.filter(({ key }) => pendingKeys.has(key));
	return [...fromUrl, ...stillPending];
}

export function useQueryParamsManager() {
	const { search } = useLocation();
	const navigate = useNavigate();
	const wantedStateStore = useSyncExternalStore(
		useCallback((callback) => {
			const subscription = QueryParamsManagerImpl.getInstance().wantedState.subscribe(callback);
			return () => subscription.unsubscribe();
		}, []),
		() => QueryParamsManagerImpl.getInstance().wantedState.getValue(),
	);

	const wantedSearch = useMemo(() => serializeQueryParams(wantedStateStore), [wantedStateStore]);

	useEffect(() => {
		const manager = QueryParamsManagerImpl.getInstance();
		const wanted = manager.wantedState.getValue();
		if (serializeQueryParams(wanted) !== search) {
			manager.wantedState.next(adoptSearch(search, wanted, manager.pendingKeys));
		}
	}, [search]);

	useEffect(() => {
		if (wantedSearch === search) return undefined;
		const timeout = setTimeout(() => {
			const manager = QueryParamsManagerImpl.getInstance();
			const currentWanted = serializeQueryParams(manager.wantedState.getValue());
			manager.pendingKeys.clear();
			if (currentWanted === window.location.search) return;
			navigate(`${window.location.pathname}${currentWanted}${window.location.hash}`, { replace: true });
		}, 0);
		return () => clearTimeout(timeout);
	}, [navigate, wantedSearch, search]);
}
